import { useEffect, useState } from 'react';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import type { PlatformMetrics, PlatformMetricsSnapshot } from '../../client/api';
import {
  BoPage,
  Section,
  ErrorBanner,
  btnGhost,
  btnPrimary,
  errMsg,
  formatBytes,
  formatDateTime,
} from './ui';

// =============================================================================
// BO · Platform monitoring (NBL_REQ_806 / HNBL-113) — live aggregates +
// snapshot history. Capture-now persists a snapshot via /platform/metrics/snapshot.
// =============================================================================

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1 rounded border border-line bg-panel-2/50 p-4">
      <span className="text-[13px] font-medium uppercase tracking-wide text-text-faint">{label}</span>
      <span className="text-[26px] font-semibold leading-none text-text">{value}</span>
      {sub && <span className="text-[13px] text-text-dim">{sub}</span>}
    </div>
  );
}

export function Platform(): JSX.Element {
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [history, setHistory] = useState<PlatformMetricsSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [m, h] = await Promise.all([
        api.boPlatformMetrics(),
        api.boPlatformHistory(20),
      ]);
      setMetrics(m);
      setHistory(h);
      setErr(null);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  async function capture() {
    setCapturing(true);
    setErr(null);
    try {
      const snap = await api.boPlatformSnapshot();
      setHistory((prev) => [snap, ...prev].slice(0, 20));
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setCapturing(false);
    }
  }

  return (
    <BoPage
      title="플랫폼 모니터링"
      subtitle="운영사 전용 — 플랫폼 전체 지표(테넌트·디바이스·스토리지)와 스냅샷 이력."
      actions={
        <>
          <button type="button" className={btnGhost} onClick={() => void load()} disabled={loading}>
            새로고침
          </button>
          <button type="button" className={btnPrimary} onClick={() => void capture()} disabled={capturing}>
            {capturing ? '저장 중…' : '스냅샷 캡처'}
          </button>
        </>
      }
    >
      <ErrorBanner message={err} />
      {loading && !metrics && !err ? <ContentSkeleton label="플랫폼 지표를 불러오는 중…" variant="cards" /> : <>
      <Section title="실시간 지표">
        {!metrics ? (
          <p className="text-[14px] text-text-dim">표시할 지표가 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Metric label="테넌트" value={String(metrics.tenantCount)} sub={`활성 ${metrics.activeTenantCount}`} />
            <Metric label="디바이스" value={String(metrics.deviceCount)} sub={`온라인 ${metrics.onlineDeviceCount}`} />
            <Metric label="사용자" value={String(metrics.userCount)} />
            <Metric label="총 스토리지" value={formatBytes(metrics.totalStorageBytes)} />
          </div>
        )}
      </Section>
      <Section title="스냅샷 이력">
        {history.length === 0 ? (
          <p className="py-6 text-center text-[14px] text-text-faint">스냅샷이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="border-b border-line bg-panel-2">
                  <th className="px-3 py-2 text-left font-semibold text-text-dim">캡처 시각</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-dim">테넌트</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-dim">활성</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-dim">디바이스</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-dim">온라인</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-dim">사용자</th>
                  <th className="px-3 py-2 text-right font-semibold text-text-dim">스토리지</th>
                </tr>
              </thead>
              <tbody>
                {history.map((s) => (
                  <tr key={s.id} className="border-b border-line last:border-0 hover:bg-panel-hover transition-colors">
                    <td className="px-3 py-2 text-text-dim">{formatDateTime(s.capturedAt)}</td>
                    <td className="px-3 py-2 text-right text-text">{s.tenantCount}</td>
                    <td className="px-3 py-2 text-right text-text-dim">{s.activeTenantCount}</td>
                    <td className="px-3 py-2 text-right text-text">{s.deviceCount}</td>
                    <td className="px-3 py-2 text-right text-text-dim">{s.onlineDeviceCount}</td>
                    <td className="px-3 py-2 text-right text-text-dim">{s.userCount}</td>
                    <td className="px-3 py-2 text-right text-text-dim">{formatBytes(s.totalStorageBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      </>}
    </BoPage>
  );
}
